import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TranslocoModule, TranslocoService, provideTranslocoScope } from '@jsverse/transloco';
import { CouponService } from '@rma/billing/public/api-coupons';
import { SubscriptionProductService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PromotionBannerStyle } from '../orc-promotion-banner/promotion-banner-style.enum';

@Component({
  selector: 'rma-default-coupon-banner',
  templateUrl: './default-coupon-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule],
  providers: [provideTranslocoScope('promotions')],
})
export class DefaultCouponBannerComponent implements OnInit {
  @Input({ required: true })
  public bannerStyle: PromotionBannerStyle;

  public label$: Observable<string | null> = of(null);

  private translationPrefix = 'default-coupon.';
  private translationScope = 'promotions';

  constructor(
    private readonly couponService: CouponService,
    private readonly subscriptionProductService: SubscriptionProductService,
    private readonly translationService: TranslocoService,
  ) {}

  ngOnInit(): void {
    const defaultLabel$ = combineLatest([this.couponService.getDefaultCoupon(), this.subscriptionProductService.availableTrialDays$]).pipe(
      switchMap(([coupon, trialDays]) => {
        const labelKey = trialDays > 0 ? `${this.translationPrefix}summary-trials` : `${this.translationPrefix}summary`;
        return coupon
          ? this.translationService.selectTranslate<string>(labelKey, coupon ?? {}, { scope: this.translationScope })
          : of(null);
      }),
      map((translation) =>
        translation && !translation.startsWith(`${this.translationScope}.${this.translationPrefix}`) ? translation : null,
      ),
    );

    const couponLabel$ = this.couponService.coupon().pipe(
      switchMap((coupon) =>
        this.translationService.selectTranslate<string>(`${this.translationPrefix}${coupon}.summary`, {}, { scope: this.translationScope }),
      ),
      map((translation) => (!translation.startsWith(`${this.translationScope}.${this.translationPrefix}`) ? translation : null)),
    );

    this.label$ = combineLatest([couponLabel$, defaultLabel$]).pipe(
      map(([couponLabel, defaultCouponLabel]) => (couponLabel !== null ? couponLabel : defaultCouponLabel)),
    );
  }
}
